import { useState } from 'react'
import { useAppStore } from '@core/store'
import {
  formatCoord,
  type CoordinateInfo,
  type ModelInfo,
} from '@viewer/lib/modelInfo'
import {
  extractElementChain,
  mapPointFromFile,
  type ElementChainResult,
} from '@viewer/lib/elementPlacement'
import {
  IDENTITY_PLACEMENT,
  placementsDiffer,
} from '@viewer/lib/geoTransform'
import type { KeyValue } from '@viewer/lib/ifcItemData'
import KeyValueTable from './KeyValueTable'

interface CoordinatesViewProps {
  coords: CoordinateInfo | null
  modelInfo: ModelInfo | null
  showGlobal: boolean
  onToggleGlobal: () => void
  ancestorMeta: Map<number, { category: string; name: string }>
  modelId: string | null
  localId: number | null
  ancestors: number[]
  elementCategory: string
  elementName: string
  /** Linhas de localização (contentor espacial: Site / Building /
   *  Storey / Space) mostradas como primeiro bloco compacto aqui, em vez
   *  de numa secção "Localização" separada. */
  location: KeyValue[]
}

export default function CoordinatesView({
  coords,
  modelInfo,
  showGlobal,
  onToggleGlobal,
  ancestorMeta,
  modelId,
  localId,
  ancestors,
  elementCategory,
  elementName,
  location,
}: CoordinatesViewProps) {
  if (!coords) {
    return (
      <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
        A calcular coordenadas…
      </div>
    )
  }

  const wp = coords.worldPosition
  const bbox = coords.bbox
  const mc = modelInfo?.crs?.mapConversion ?? null
  // The viewer now bakes IfcMapConversion onto model.object at load
  // (geo-referenced federation), so `wp` is already in Three-world
  // coords with the map transform applied. Recovering map CRS coords
  // (E, N, h) is just an axis remap: in IFC convention Z is up and Y
  // is north; in Three Y is up and -Z is north. So:
  //   E = wp.x        N = -wp.z        h = wp.y
  // No more matrix math at this layer; that's done at load time.
  const global = wp && mc ? { x: wp.x, y: -wp.z, z: wp.y } : null

  return (
    <div className="space-y-2 text-[11px]">
      {location.length > 0 && (
        <div className="rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Localização
          </div>
          <div className="mt-0.5">
            <KeyValueTable rows={location} />
          </div>
        </div>
      )}

      <div className="rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 px-2 py-1.5">
        <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Posição world (modelo)
        </div>
        {wp ? (
          <div className="mt-0.5 grid grid-cols-3 gap-1 font-mono">
            <span>X = {formatCoord(wp.x)}</span>
            <span>Y = {formatCoord(wp.y)}</span>
            <span>Z = {formatCoord(wp.z)}</span>
          </div>
        ) : (
          <div className="mt-0.5 italic text-[var(--color-text-muted)]">
            (não disponível)
          </div>
        )}
      </div>

      {bbox && (
        <div className="rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Bounding box (world)
          </div>
          <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 font-mono">
            <span className="text-[var(--color-text-muted)]">min</span>
            <span>
              [{formatCoord(bbox.min.x)}, {formatCoord(bbox.min.y)},{' '}
              {formatCoord(bbox.min.z)}]
            </span>
            <span className="text-[var(--color-text-muted)]">max</span>
            <span>
              [{formatCoord(bbox.max.x)}, {formatCoord(bbox.max.y)},{' '}
              {formatCoord(bbox.max.z)}]
            </span>
            <span className="text-[var(--color-text-muted)]">size</span>
            <span>
              [{formatCoord(bbox.max.x - bbox.min.x)},{' '}
              {formatCoord(bbox.max.y - bbox.min.y)},{' '}
              {formatCoord(bbox.max.z - bbox.min.z)}]
            </span>
            <span
              className="text-[var(--color-text-muted)]"
              title="Volume da caixa envolvente alinhada aos eixos do mundo, não o volume do elemento. Calculado da geometria no motor, que trabalha em metros."
            >
              volume da caixa
            </span>
            <span>
              {formatCoord(
                Math.max(0, bbox.max.x - bbox.min.x) *
                  Math.max(0, bbox.max.y - bbox.min.y) *
                  Math.max(0, bbox.max.z - bbox.min.z),
                4,
              )}{' '}
              m³ (calculado)
            </span>
          </div>
        </div>
      )}

      {wp && mc && (
        <div className="rounded border border-emerald-700/50 bg-[var(--color-ok-bg)] px-2 py-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-emerald-400/90">
              CRS global (via IfcMapConversion)
            </span>
            <button
              type="button"
              onClick={onToggleGlobal}
              className="rounded border border-emerald-700/50 px-1.5 py-0.5 text-[10px] text-[var(--color-ok-text)] hover:bg-[var(--color-ok-bg)]"
            >
              {showGlobal ? 'Esconder' : 'Calcular'}
            </button>
          </div>
          {showGlobal && global && (
            <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 font-mono text-emerald-100">
              <span className="text-emerald-400/80">E</span>
              <span>{formatCoord(global.x)}</span>
              <span className="text-emerald-400/80">N</span>
              <span>{formatCoord(global.y)}</span>
              <span className="text-emerald-400/80">h</span>
              <span>{formatCoord(global.z)}</span>
            </div>
          )}
        </div>
      )}

      {wp && !mc && (
        <div className="rounded border border-[var(--color-border)]/40 bg-[var(--color-bg)]/30 px-2 py-1 text-[10px] text-[var(--color-text-muted)]">
          Sem IfcMapConversion no modelo, não é possível converter para CRS
          global.
        </div>
      )}

      {coords.ancestorPositions.length > 0 && (
        <details className="rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 px-2 py-1.5">
          <summary
            className="cursor-pointer text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] marker:text-[var(--color-text-dim)]"
            title="Posição de cada contentor espacial acima do elemento (IfcSite / IfcBuilding / IfcBuildingStorey / IfcSpace). O Fragments não preserva o IfcLocalPlacement dos contentores, por isso a posição mostrada é o centro geométrico dos elementos contidos, em coordenadas world. A cadeia de referenciais exata (bloco principal em baixo) dá as origens verdadeiras."
          >
            aproximação: centro geométrico dos contidos
          </summary>
          <div className="mt-1 space-y-0.5 font-mono text-[10px]">
            {coords.ancestorPositions.map((a, i) => {
              const meta = ancestorMeta.get(a.localId)
              const label = meta
                ? `${meta.category}${meta.name ? ` · ${meta.name}` : ''}`
                : `#${a.localId}`
              return (
                <div
                  key={a.localId}
                  className="grid grid-cols-[1fr_auto] gap-x-2"
                  style={{ paddingLeft: `${i * 8}px` }}
                >
                  <span className="truncate text-[var(--color-text-muted)]">
                    {label}
                  </span>
                  <span className="text-[var(--color-text)]">
                    {a.worldPosition
                      ? `[${formatCoord(a.worldPosition.x, 2)}, ${formatCoord(a.worldPosition.y, 2)}, ${formatCoord(a.worldPosition.z, 2)}]`
                      : '(sem geometria)'}
                  </span>
                </div>
              )
            })}
          </div>
          <div className="mt-1 text-[9px] text-[var(--color-text-dim)]">
            Centro geométrico dos elementos contidos, não a origem do
            IfcLocalPlacement. Para a cadeia de referenciais exata, usa o
            bloco em baixo.
          </div>
        </details>
      )}

      {modelId && localId != null && (
        <ExactChainBlock
          key={`${modelId}:${localId}`}
          modelId={modelId}
          localId={localId}
          ancestors={ancestors}
          ancestorMeta={ancestorMeta}
          elementCategory={elementCategory}
          elementName={elementName}
        />
      )}
    </div>
  )
}

// Cadeia de referenciais EXATA do elemento (lida do STEP): a que está
// ancorado cada nível (elemento → storey → building → site → origem do
// ficheiro), com origens relativas e absolutas em metros e, quando o
// modelo está georreferenciado, a projeção de cada origem no CRS do
// mapa. Responde a "as coordenadas desta parede são face ao piso ou a
// outra referência?" com os valores exatos do ficheiro.
function ExactChainBlock({
  modelId,
  localId,
  ancestors,
  ancestorMeta,
  elementCategory,
  elementName,
}: {
  modelId: string
  localId: number
  ancestors: number[]
  ancestorMeta: Map<number, { category: string; name: string }>
  elementCategory: string
  elementName: string
}) {
  const [result, setResult] = useState<ElementChainResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const placement = useAppStore(
    (s) => s.geoOverrides[modelId] ?? s.geoBaselines[modelId] ?? null,
  )
  const crs = useAppStore(
    (s) => s.crsOverrides[modelId] ?? s.crsMeta[modelId] ?? null,
  )
  const geoActive =
    !!placement && placementsDiffer(placement, IDENTITY_PLACEMENT)

  const run = () => {
    setBusy(true)
    setFailed(false)
    extractElementChain(modelId, localId)
      .then((r) => {
        setResult(r)
        if (!r) setFailed(true)
      })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false))
  }

  // Papel semântico de cada nível: chain[0] = o próprio elemento;
  // chain[1..] = contentores, do mais próximo para a raiz (o ancestor
  // path do viewerApi vem raiz → folha, por isso anda-se do fim).
  const roleOf = (index: number): string => {
    if (index === 0) {
      const name = elementName && elementName !== '-' ? elementName : ''
      return `${elementCategory || 'Elemento'}${name ? ' · ' + name : ''}`
    }
    const ancestorIdx = ancestors.length - index
    if (ancestorIdx >= 0 && ancestorIdx < ancestors.length) {
      const aId = ancestors[ancestorIdx]
      const m = ancestorMeta.get(aId)
      if (m) return `${m.category}${m.name && m.name !== '-' ? ' · ' + m.name : ''}`
      return `#${aId}`
    }
    return 'Referencial'
  }

  return (
    <div className="rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 px-2 py-1.5">
      <div className="flex items-center justify-between">
        <span
          className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]"
          title="Cadeia IfcLocalPlacement lida do próprio ficheiro (parser paralelo): a que referencial está ancorado cada nível, com a origem relativa ao pai e a absoluta no referencial do ficheiro, em metros. Com o modelo georreferenciado, mostra também cada origem no CRS do mapa."
        >
          Cadeia de referenciais (exata)
        </span>
        {!result && (
          <button
            type="button"
            onClick={run}
            disabled={busy}
            className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
            title="Lê a cadeia IfcLocalPlacement do STEP via o parser paralelo (a primeira vez pode custar segundos em modelos grandes)."
          >
            {busy ? 'A ler IFC…' : 'Calcular'}
          </button>
        )}
      </div>
      {failed && (
        <div className="mt-1 text-[10px] italic text-[var(--color-text-muted)]">
          Sem ObjectPlacement legível para este elemento.
        </div>
      )}
      {result && (
        <div className="mt-1 space-y-1">
          <div className="text-[9px] text-[var(--color-text-dim)]">
            Da raiz para o elemento; coordenadas do ficheiro (x=E, y=N,
            z=h) em metros{geoActive && crs ? `; mapa em ${crs}` : ''}.
          </div>
          {[...result.rows]
            .map((r, i) => ({ row: r, chainIndex: i }))
            .reverse()
            .map(({ row, chainIndex }) => {
              const mapPt =
                geoActive && placement
                  ? mapPointFromFile(placement, row.absolute)
                  : null
              // Pai = o nível imediatamente acima na hierarquia (o
              // próximo da cadeia folha->raiz). O nível mais externo está
              // ancorado à origem do próprio ficheiro.
              const parentRole =
                chainIndex + 1 < result.rows.length
                  ? roleOf(chainIndex + 1)
                  : 'origem do ficheiro'
              return (
                <div
                  key={`${row.placementID ?? chainIndex}`}
                  className="rounded border border-[var(--color-border)]/40 bg-[var(--color-bg)]/30 px-1.5 py-1"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-[10px] font-medium text-[var(--color-text)]">
                      {roleOf(chainIndex)}
                    </span>
                    {row.placementID !== null && (
                      <span className="shrink-0 font-mono text-[9px] text-[var(--color-text-dim)]">
                        placement #{row.placementID}
                      </span>
                    )}
                  </div>
                  <div className="text-[9px] text-[var(--color-text-dim)]">
                    ancorado a:{' '}
                    <span className="text-[var(--color-text-muted)]">
                      {parentRole}
                    </span>
                  </div>
                  <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 font-mono text-[10px] text-[var(--color-text-muted)]">
                    <span>rel. ao pai</span>
                    <span className="text-[var(--color-text)]">
                      {row.relative
                        ? `[${row.relative.map((n) => formatCoord(n)).join(', ')}] m`
                        : '-'}
                      {row.rotationDeg !== null &&
                        Math.abs(row.rotationDeg) > 1e-9 &&
                        `  ·  rot ${formatCoord(row.rotationDeg, 3)}°`}
                    </span>
                    <span>abs. ficheiro</span>
                    <span className="text-[var(--color-text)]">
                      E {formatCoord(row.absolute[0])} · N{' '}
                      {formatCoord(row.absolute[1])} · h{' '}
                      {formatCoord(row.absolute[2])}
                    </span>
                    {mapPt && (
                      <>
                        <span>no mapa</span>
                        <span className="text-[var(--color-ok-text)]">
                          E {formatCoord(mapPt[0])} · N {formatCoord(mapPt[1])}{' '}
                          · h {formatCoord(mapPt[2])}
                        </span>
                      </>
                    )}
                    {(row.axisZ || row.axisX) && (
                      <>
                        <span
                          title="Eixos do IfcAxis2Placement3D deste nível (Axis=Z, RefDirection=X), ratios crus do ficheiro. Só aparece quando o nível os declara (senão usa os defaults Z=[0,0,1], X=[1,0,0])."
                        >
                          eixos
                        </span>
                        <span className="text-[var(--color-text)]">
                          Z [
                          {(row.axisZ ?? [0, 0, 1])
                            .map((n) => formatCoord(n, 4))
                            .join(', ')}
                          ] · X [
                          {(row.axisX ?? [1, 0, 0])
                            .map((n) => formatCoord(n, 4))
                            .join(', ')}
                          ]
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          {!geoActive && (
            <div className="text-[9px] text-[var(--color-text-dim)]">
              Sem georreferenciação ativa (MapConversion ou posição manual),
              não há projeção para o mapa.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
